import { Dataset, DataFlowDSLAdapterRegistry, LinkedService, Metamodel, Owned } from 'shell-models-lib';
import { DataFlowExpressionUtils } from '../expression.util';
import { DataFlowSource } from 'app/model/dataflow/dataflow-source';
import { XmlSettings } from 'app/model/dataflow/dataset-settings/xml-settings';
import { FileBasedSettingsAdapter } from './file-based-settings.adapter';
import { StringDataFlowType } from 'app/model/dataflow/dataflow-datatype';
import { DatasetFormatType, DataFlowDatasetSettings } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import { DataFlowAdapterUtils } from '../adapter.util';
import { DataFlowExpression } from 'app/model/dataflow/dataflowexpression';
import { getAttributeValueFromDataset } from 'app/model/dataset/dataset.utils';

@DataFlowDSLAdapterRegistry.adapter({
    model: Metamodel.modelFor(XmlSettings)
})
export class XmlFormatAdapter extends FileBasedSettingsAdapter {
    /**
     * Supplements missing DSL properties for Fabric Migration.
     */
    public supplementPropsForFabricMigration(
        dataset: Dataset | undefined, linkedService: LinkedService | undefined, props: string[], isSource: boolean,
        isInline?: boolean, datasetSettings?: DataFlowDatasetSettings, _format?: string, wildcardPaths?: DataFlowExpression[]
    ): void {
        if (isInline || !dataset) {
            return;
        }

        props.push(`format: '${DatasetFormatType.Xml}'`);

        DataFlowAdapterUtils.addCommonPropsForFileBasedFormats(dataset, linkedService, props, isSource, isInline, datasetSettings, wildcardPaths);

        const compressionCodec = getAttributeValueFromDataset(dataset, 'compressionCodec');
        if (compressionCodec) {
            props.push(`compressionType: '${compressionCodec}'`);
            const compressionLevel = getAttributeValueFromDataset(dataset, 'compressionLevel');
            if (compressionLevel) {
                props.push(`compressionLevel: ${DataFlowAdapterUtils.formatExpressionValue(compressionLevel)}`);
            }
        }

        const encodingName = getAttributeValueFromDataset(dataset, 'encodingName');
        if (encodingName) {
            props.push(`encodingName: ${DataFlowAdapterUtils.formatExpressionValue(encodingName)}`);
        }

        const nullValue = getAttributeValueFromDataset(dataset, 'nullValue');
        if (nullValue) {
            props.push(`nullValue: ${DataFlowAdapterUtils.formatExpressionValue(nullValue)}`);
        }
    }

    protected async toSourceDSL(xmlSettings: XmlSettings): Promise<string[]> {
        const props = [];
        props.push(...(await super.toSourceDSL(xmlSettings)));
        const model = xmlSettings.owner as DataFlowSource;
        if (xmlSettings.compressionType) {
            props.push(`compressionType: '${xmlSettings.compressionType}'`);
            props.push(
                `compressionLevel: ${DataFlowExpressionUtils.wrapExpressionConfigValue(xmlSettings.compressionLevel)}`
            );
        }
        if (xmlSettings.encoding.expression) {
            props.push(`encodingName: ${DataFlowExpressionUtils.wrapExpressionConfigValue(xmlSettings.encoding)}`);
        }
        if (xmlSettings.nullValue.expression) {
            props.push(`nullValue: ${DataFlowExpressionUtils.wrapExpressionConfigValue(xmlSettings.nullValue)}`);
        }
        if (model.validationMode.expression) {
            props.push(`validationMode: ${DataFlowExpressionUtils.wrapExpressionConfigValue(model.validationMode)}`);
        }
        props.push(`namespaces: ${model.namespaces}`);
        if (model.namespaces && model.namespacePrefixes && model.namespacePrefixes.length > 0) {
            const namespacePrefixes = model.namespacePrefixes
                .filter((namespacePrefix) => namespacePrefix.key && namespacePrefix.val)
                .map(
                    (namespacePrefix) =>
                        // eslint-disable-next-line max-len
                        `${DataFlowExpressionUtils.wrapExpressionConfigValue(
                            model.createTemplatableExpression(StringDataFlowType.TYPE, namespacePrefix.key)
                        )}->${DataFlowExpressionUtils.wrapExpressionConfigValue(
                            model.createTemplatableExpression(StringDataFlowType.TYPE, namespacePrefix.val)
                        )}`
                )
                .join(',');
            if (namespacePrefixes.length > 0) {
                props.push(`namespacePrefixes: [${namespacePrefixes}]`);
            }
        }

        return props;
    }

    protected async toSourceModel(
        owner: DataFlowSource,
        configs: Map<string, string>,
        handledConfigs: string[]
    ): Promise<XmlSettings> {
        const xmlSettings = (await super.toSourceModel(owner, configs, handledConfigs)) as XmlSettings;
        if (configs.has('compressionType')) {
            xmlSettings.compressionType = DataFlowExpressionUtils.extractStringConfigValue(
                configs.get('compressionType')
            );
            handledConfigs.push('compressionType');
        }
        if (configs.has('compressionLevel')) {
            xmlSettings.compressionLevel = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('compressionLevel'),
                owner
            );
            handledConfigs.push('compressionLevel');
        }
        if (configs.has('encodingName')) {
            xmlSettings.encoding = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('encodingName'),
                owner
            );
            handledConfigs.push('encodingName');
        }
        if (configs.has('nullValue')) {
            xmlSettings.nullValue = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('nullValue'),
                owner
            );
            handledConfigs.push('nullValue');
        }
        return xmlSettings;
    }

    protected _generateSetting(owner: DataFlowSource): XmlSettings {
        return Owned.create<XmlSettings>(XmlSettings.MODEL, owner);
    }
}
